import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { makeRequest } from "../../utils/request";

export type TtsLabAggregate = {
  provider: string;
  // Empty string for samples recorded before the agents stamped a model
  // label — rendered as "—".
  model: string;
  ttfaP50: number | null;
  ttfaP95: number | null;
  durationP50: number | null;
  durationP95: number | null;
  sampleCount: number;
};

export type TtsLabState = {
  aggregates: TtsLabAggregate[];
  loading: boolean;
  error: string | null;
};

export type GetTtsAggregatesParams = {
  startDate: string;
  endDate: string;
  provider?: string;
};

export const getAggregates = createAsyncThunk("ttsLab/getAggregates", async (params: GetTtsAggregatesParams) => {
  const queryParams = new URLSearchParams({
    startDate: params.startDate,
    endDate: params.endDate,
  });
  if (params.provider) queryParams.append("provider", params.provider);

  const { data } = await makeRequest<{ data: TtsLabAggregate[] }>(`/v1/tts-lab/aggregates?${queryParams}`);
  return data;
});

const initialState: TtsLabState = {
  aggregates: [],
  loading: false,
  error: null,
};

const ttsLabSlice = createSlice({
  name: "ttsLab",
  initialState,
  reducers: {
    clear: () => initialState,
  },
  extraReducers: (builder) => {
    builder.addCase(getAggregates.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(getAggregates.fulfilled, (state, action) => {
      state.loading = false;
      state.aggregates = action.payload;
    });
    builder.addCase(getAggregates.rejected, (state, action) => {
      state.loading = false;
      state.error = action.error.message || "Failed to fetch TTS Lab aggregates";
    });
  },
});

export const { clear } = ttsLabSlice.actions;
export const ttsLabReducer = ttsLabSlice.reducer;
